import { MarketingNav } from '@/components/layout/marketing-nav'
import { SiteFooter } from '@/components/layout/site-footer'
import { BreadcrumbJsonLd } from '@/components/seo/breadcrumb-json-ld'
import { PageMeta } from '@/components/seo/page-meta'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import apiClient from '@/lib/api-client'
import { PELGO_CONTACT_SEO } from '@/lib/pelgo-seo'
import { executeRecaptcha, preloadRecaptcha } from '@/lib/recaptcha'
import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

const contactSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  email: z.string().email('Invalid email address'),
  company: z.string().min(1, 'Company name is required'),
  phone: z.string().optional(),
  message: z.string().min(10, 'Message must be at least 10 characters'),
  // Honeypot. Hidden from real users, so a non-empty value means a bot.
  // Deliberately unvalidated: the backend decides what to do with it.
  website: z.string().optional(),
})

type ContactFormData = z.infer<typeof contactSchema>

export function ContactPage() {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitSuccess, setSubmitSuccess] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  // Warm the reCAPTCHA script so the first submit is not blocked on loading it.
  useEffect(() => {
    preloadRecaptcha()
  }, [])

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
  })

  const onSubmit = async (data: ContactFormData) => {
    setIsSubmitting(true)
    setSubmitError(null)
    setSubmitSuccess(false)

    try {
      const recaptchaToken = await executeRecaptcha('contact')

      await apiClient.post('/api/v1/contact', {
        name: data.name,
        email: data.email,
        company: data.company,
        phone: data.phone || undefined,
        message: data.message,
        recaptcha_token: recaptchaToken || undefined,
        website: data.website || undefined,
      })

      setSubmitSuccess(true)
      reset()

      // Reset success message after 5 seconds
      setTimeout(() => {
        setSubmitSuccess(false)
      }, 5000)
    } catch (error) {
      console.error('Contact form submission failed:', error)
      setSubmitError(
        'Failed to send message. Please try again or email us directly at support@pelgo.com.',
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="flex flex-col min-h-screen bg-white">
      <PageMeta config={PELGO_CONTACT_SEO} />
      <BreadcrumbJsonLd
        items={[
          { name: 'Home', path: '/' },
          { name: 'Contact', path: '/contact' },
        ]}
      />
      <MarketingNav variant="standalone" />

      {/* Main Content */}
      <div className="flex-1 pt-32">
        {/* Contact Section */}
        <section
          className="py-16 md:py-32 scroll-fade-up"
          style={{
            backgroundImage:
              'linear-gradient(rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.04) 100%), linear-gradient(90deg, rgb(255, 255, 255) 0%, rgb(255, 255, 255) 100%)',
          }}
        >
          <div className="container mx-auto px-4 md:px-16">
            <div className="max-w-[1280px] mx-auto">
              <div className="flex flex-col md:flex-row gap-8 md:gap-16 items-start">
                {/* Left Side - Text Content */}
                <div className="flex-1 flex flex-col gap-8">
                  <div className="flex flex-col gap-6">
                    <div className="h-3 w-12 bg-pelgo-blue-light rounded-full" />
                    <h1 className="text-4xl md:text-[48px] font-display text-gray-900 leading-[1.2] tracking-[-0.96px]">
                      Contact Us
                    </h1>
                    <p className="text-base font-medium text-gray-500 tracking-[-0.32px] leading-relaxed">
                      Need help? Ask product questions, report problems, or share your feedback.
                    </p>
                  </div>
                  <div className="flex flex-col gap-4">
                    <div className="flex items-start gap-3">
                      <svg
                        className="h-5 w-5 text-pelgo-blue-light flex-shrink-0 mt-0.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      <p className="text-base font-medium text-gray-900 tracking-[-0.32px] leading-relaxed">
                        Inquiries answered within 24 hours
                      </p>
                    </div>
                    <div className="flex items-start gap-3">
                      <svg
                        className="h-5 w-5 text-pelgo-blue-light flex-shrink-0 mt-0.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      <p className="text-base font-medium text-gray-900 tracking-[-0.32px] leading-relaxed">
                        Identify the right plan for your team
                      </p>
                    </div>
                    <div className="flex items-start gap-3">
                      <svg
                        className="h-5 w-5 text-pelgo-blue-light flex-shrink-0 mt-0.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      <p className="text-base font-medium text-gray-900 tracking-[-0.32px] leading-relaxed">
                        Receive onboarding assistance
                      </p>
                    </div>
                  </div>
                </div>

                {/* Right Side - Contact Form */}
                <div className="flex-1 w-full">
                  <div className="bg-white border border-gray-200 rounded-2xl shadow-[0px_2px_4px_0px_rgba(0,0,0,0.1)] p-6 md:p-8">
                    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                      {/* Honeypot: hidden from real users, bots fill it in.
                          Uses off-screen positioning rather than `type=hidden`
                          or `display:none`, which bots skip. */}
                      <div className="absolute left-[-9999px] top-auto h-px w-px overflow-hidden">
                        <Label htmlFor="website">Website</Label>
                        <Input
                          id="website"
                          type="text"
                          tabIndex={-1}
                          autoComplete="off"
                          aria-hidden="true"
                          {...register('website')}
                        />
                      </div>

                      {/* Name Field */}
                      <div className="space-y-2">
                        <Label htmlFor="name" className="text-sm font-medium text-gray-900">
                          Name *
                        </Label>
                        <Input
                          id="name"
                          type="text"
                          placeholder="John Doe"
                          {...register('name')}
                          className="w-full"
                          aria-invalid={errors.name ? 'true' : 'false'}
                        />
                        {errors.name && (
                          <p className="text-sm text-red-600">{errors.name.message}</p>
                        )}
                      </div>

                      {/* Email Field */}
                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-sm font-medium text-gray-900">
                          Email *
                        </Label>
                        <Input
                          id="email"
                          type="email"
                          placeholder="you@example.com"
                          {...register('email')}
                          className="w-full"
                          aria-invalid={errors.email ? 'true' : 'false'}
                        />
                        {errors.email && (
                          <p className="text-sm text-red-600">{errors.email.message}</p>
                        )}
                      </div>

                      {/* Company Field */}
                      <div className="space-y-2">
                        <Label htmlFor="company" className="text-sm font-medium text-gray-900">
                          Company *
                        </Label>
                        <Input
                          id="company"
                          type="text"
                          placeholder="Your Company"
                          {...register('company')}
                          className="w-full"
                          aria-invalid={errors.company ? 'true' : 'false'}
                        />
                        {errors.company && (
                          <p className="text-sm text-red-600">{errors.company.message}</p>
                        )}
                      </div>

                      {/* Phone Field (Optional) */}
                      <div className="space-y-2">
                        <Label htmlFor="phone" className="text-sm font-medium text-gray-900">
                          Phone
                        </Label>
                        <Input
                          id="phone"
                          type="tel"
                          placeholder="(555) 123-4567"
                          {...register('phone')}
                          className="w-full"
                          aria-invalid={errors.phone ? 'true' : 'false'}
                        />
                        {errors.phone && (
                          <p className="text-sm text-red-600">{errors.phone.message}</p>
                        )}
                      </div>

                      {/* Message Field */}
                      <div className="space-y-2">
                        <Label htmlFor="message" className="text-sm font-medium text-gray-900">
                          Message *
                        </Label>
                        <Textarea
                          id="message"
                          placeholder="Tell us about your needs..."
                          rows={6}
                          {...register('message')}
                          className="w-full resize-none"
                          aria-invalid={errors.message ? 'true' : 'false'}
                        />
                        {errors.message && (
                          <p className="text-sm text-red-600">{errors.message.message}</p>
                        )}
                      </div>

                      {/* Success Message */}
                      {submitSuccess && (
                        <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                          <p className="text-sm font-medium text-green-800">
                            Thank you! Your message has been sent successfully. We'll get back to
                            you soon.
                          </p>
                        </div>
                      )}

                      {/* Error Message */}
                      {submitError && (
                        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                          <p className="text-sm font-medium text-red-800">{submitError}</p>
                        </div>
                      )}

                      {/* Submit Button */}
                      <Button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full bg-pelgo-blue-light hover:bg-pelgo-blue-light/90 text-white rounded-full px-6 py-3 text-base font-semibold min-h-[48px]"
                      >
                        {isSubmitting ? 'Sending...' : 'Send Message'}
                      </Button>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      <SiteFooter />
    </div>
  )
}
